import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useFlags } from "launchdarkly-react-client-sdk";
import DocumentPreviewModal from "./DocumentPreviewModal.jsx";
import { closeDocumentPreviewModal } from "../../redux/documentPreviewModalSlice.js";
import { fetchPolicyScores, applyPolicyFeedbacks, ignorePolicyItem } from "../../redux/policyScoreSlice.js";
import {
  formatTopicKey,
  getScoreLabel,
  getScoreBadgeClass,
  isDreamsSuggestion,
  questionForDreamsItem,
} from "../../utils/policyFormatters.js";
import renderPolicyContent from "../../utils/renderPolicyContent.jsx";
import { buildPolicyMarkdown, buildTopicMarkdown, policyFileName, downloadTextFile } from "../../utils/policyExport.js";

// Single shared instance of DocumentPreviewModal, rendered once at the layout root (see
// layouts/ContentLayout.jsx) so it stays mounted across route changes — mirrors
// GlobalIssuesDetectedModal. Its open/close flag and the underlying policy topic/score
// data live in Redux (documentPreviewModalSlice / policyScoreSlice); the tab/dropdown/
// checklist interaction state stays local here since it's UI-only and this component
// itself never unmounts, so it already survives navigation without needing the store.
const GlobalDocumentPreviewModal = () => {
  const dispatch = useDispatch();
  const { enablePolicyCategorization } = useFlags();
  const isOpen = useSelector(state => state.documentPreviewModal.isOpen);
  const { policyTopics, overallScore } = useSelector(state => state.policyScore);

  const [activeDocTab, setActiveDocTab] = useState(0);
  const [tabDropdownOpen, setTabDropdownOpen] = useState(false);
  // Keyed by topic key so expanding a card on one tab doesn't expand it on every tab.
  const [suggestedOpenMap, setSuggestedOpenMap] = useState({});
  const [issuesOpenMap, setIssuesOpenMap] = useState({});
  const [checkedFeedbacks, setCheckedFeedbacks] = useState(new Set());
  const [answerInputs, setAnswerInputs] = useState({});
  // Track which topic's apply is in flight (rather than a plain boolean) so the
  // "Applying..." state only shows on the tab that triggered it, not every tab.
  const [applyingKey, setApplyingKey] = useState(null);
  const [applyingIssuesKey, setApplyingIssuesKey] = useState(null);
  // Same "which tab triggered this" tracking as applyingKey/applyingIssuesKey,
  // for the bulk Ignore button that sits next to each card's Apply button.
  const [ignoringKey, setIgnoringKey] = useState(null);
  const [ignoringIssuesKey, setIgnoringIssuesKey] = useState(null);

  useEffect(() => {
    setCheckedFeedbacks(new Set());
  }, [activeDocTab]);

  // Reset to the first tab each time the modal is opened, so a tab switched to on a
  // previous visit doesn't stick around — the component stays mounted across opens/closes.
  useEffect(() => {
    if (isOpen) setActiveDocTab(0);
  }, [isOpen]);

  if (!enablePolicyCategorization || !isOpen) return null;

  const currentTopic = policyTopics[activeDocTab] || null;
  // The two cards map to the two producers, one per response field — NOT to a
  // status filter on a single list. `gaps` is the scoring agent's output ("your
  // document does not answer this"); `feedbacks` is Dreams' ("your drafts keep
  // getting corrected on this"). Each backend producer writes only its own
  // field, so splitting here by source keeps them from being mixed.
  const suggestedItems = currentTopic ? currentTopic.sections.flatMap(s => s.gaps || []) : [];
  const issueItems = currentTopic ? currentTopic.sections.flatMap(s => s.feedbacks || []) : [];
  const topicSections = currentTopic ? currentTopic.sections : [];
  const topicScore = currentTopic ? Math.round(currentTopic.score) : null;
  const isApplying = applyingKey !== null && applyingKey === currentTopic?.key;
  const isApplyingIssues = applyingIssuesKey !== null && applyingIssuesKey === currentTopic?.key;
  const isIgnoring = ignoringKey !== null && ignoringKey === currentTopic?.key;
  const isIgnoringIssues = ignoringIssuesKey !== null && ignoringIssuesKey === currentTopic?.key;
  const suggestedOpen = currentTopic ? !!suggestedOpenMap[currentTopic.key] : false;
  const issuesOpen = currentTopic ? !!issuesOpenMap[currentTopic.key] : false;

  const setSuggestedOpen = updater => {
    const key = currentTopic?.key;
    if (!key) return;
    setSuggestedOpenMap(prev => ({
      ...prev,
      [key]: typeof updater === "function" ? updater(!!prev[key]) : updater,
    }));
  };

  const setIssuesOpen = updater => {
    const key = currentTopic?.key;
    if (!key) return;
    setIssuesOpenMap(prev => ({
      ...prev,
      [key]: typeof updater === "function" ? updater(!!prev[key]) : updater,
    }));
  };

  // Drop the applied items' keys from checkedFeedbacks/answerInputs — leaves any other
  // in-progress selections (e.g. from the other card) untouched.
  const clearAppliedKeys = appliedKeys => {
    setCheckedFeedbacks(prev => new Set([...prev].filter(k => !appliedKeys.has(k))));
    setAnswerInputs(prev => {
      const next = { ...prev };
      appliedKeys.forEach(k => delete next[k]);
      return next;
    });
  };

  const handleApplyFeedbacks = async () => {
    if (checkedFeedbacks.size === 0 || applyingKey !== null) return;
    const sectionKey = policyTopics[activeDocTab]?.key;
    if (!sectionKey) return;

    // Send the LLM's question + the owner's answer for each checked item. Items
    // without a typed answer fall back to their label so nothing is silently dropped.
    const answers = suggestedItems
      .filter(item => checkedFeedbacks.has(item.key))
      .map(item => ({
        key: item.key,
        // A Dreams item has no question of its own; `item.label` is only a short
        // heading ("Return shipping costs"), which is not an instruction the apply
        // route can act on. Spell the action out instead.
        question: isDreamsSuggestion(item) ? questionForDreamsItem(item) : item.question || item.label,
        answer: (answerInputs[item.key] || "").trim(),
      }))
      .filter(a => a.answer);

    if (answers.length === 0) {
      toast.error("Please answer at least one question before applying");
      return;
    }

    setApplyingKey(sectionKey);
    try {
      const result = await dispatch(applyPolicyFeedbacks({ sectionKey, answers }));
      if (applyPolicyFeedbacks.rejected.match(result)) {
        toast.error(result.payload || "Failed to apply improvements");
        return;
      }
      toast.success("Improvements applied successfully");
      clearAppliedKeys(new Set(answers.map(a => a.key)));
      dispatch(fetchPolicyScores());
    } finally {
      setApplyingKey(null);
    }
  };

  const handleApplyIssueFeedbacks = async () => {
    if (applyingIssuesKey !== null) return;
    const sectionKey = policyTopics[activeDocTab]?.key;
    if (!sectionKey) return;

    // Same shape as handleApplyFeedbacks, scoped to the Issues & Questions gaps
    // so this button can be applied independently of Suggested Improvements.
    // NOT filtered on `gap.question`: a Dreams suggestion deliberately carries no
    // question — the card states the finding and pre-fills the policy text instead
    // of restating the rule as a sentence to answer. Requiring one dropped every
    // Dreams item before the check below, so Apply always reported "answer at least
    // one question" no matter how many were ticked. A scorer gap still needs its
    // question, which it always has.
    const answers = issueItems
      .filter(gap => checkedFeedbacks.has(gap.key))
      .filter(gap => isDreamsSuggestion(gap) || (gap.question || "").trim())
      .map(gap => ({
        key: gap.key,
        // The apply route pairs question+answer into a policy rewrite. For a
        // Dreams item the instruction is the action itself, so state it plainly.
        question: (gap.question || "").trim() || questionForDreamsItem(gap),
        answer: (answerInputs[gap.key] || "").trim(),
      }))
      .filter(a => a.answer);

    if (answers.length === 0) {
      toast.error("Please answer at least one question before applying");
      return;
    }

    setApplyingIssuesKey(sectionKey);
    try {
      const result = await dispatch(applyPolicyFeedbacks({ sectionKey, answers }));
      if (applyPolicyFeedbacks.rejected.match(result)) {
        toast.error(result.payload || "Failed to apply improvements");
        return;
      }
      toast.success("Improvements applied successfully");
      clearAppliedKeys(new Set(answers.map(a => a.key)));
      dispatch(fetchPolicyScores());
    } finally {
      setApplyingIssuesKey(null);
    }
  };

  // Dismiss every checked item from a card in one go. The server hides ignored
  // items from subsequent reads, so a refetch is what removes them from the list —
  // no local filtering, which keeps this in step with Apply.
  const handleIgnoreItems = async items => {
    const itemsToIgnore = items.filter(item => item.id);
    if (itemsToIgnore.length === 0) return;
    const results = await Promise.all(itemsToIgnore.map(item => dispatch(ignorePolicyItem({ itemId: item.id }))));
    const failed = results.filter(result => ignorePolicyItem.rejected.match(result));
    if (failed.length === itemsToIgnore.length) {
      toast.error(failed[0].payload || "Failed to ignore items");
      return false;
    }
    if (failed.length > 0) {
      toast.error("Some items could not be ignored");
    } else {
      toast.success("Ignored successfully");
    }
    clearAppliedKeys(new Set(itemsToIgnore.map(item => item.key)));
    dispatch(fetchPolicyScores());
    return true;
  };

  const handleIgnoreFeedbacks = async () => {
    if (ignoringKey !== null) return;
    const sectionKey = policyTopics[activeDocTab]?.key;
    const checkedItems = suggestedItems.filter(item => checkedFeedbacks.has(item.key));
    if (!sectionKey || checkedItems.length === 0) return;

    setIgnoringKey(sectionKey);
    try {
      await handleIgnoreItems(checkedItems);
    } finally {
      setIgnoringKey(null);
    }
  };

  const handleIgnoreIssueFeedbacks = async () => {
    if (ignoringIssuesKey !== null) return;
    const sectionKey = policyTopics[activeDocTab]?.key;
    const checkedItems = issueItems.filter(gap => checkedFeedbacks.has(gap.key));
    if (!sectionKey || checkedItems.length === 0) return;

    setIgnoringIssuesKey(sectionKey);
    try {
      await handleIgnoreItems(checkedItems);
    } finally {
      setIgnoringIssuesKey(null);
    }
  };

  // Download the categorized policy content as Markdown — the whole set of topics,
  // or just the topic on screen. Built client-side from the content already in the
  // store (same text the modal renders), so no extra round-trip.
  const handleDownloadAll = () => {
    if (policyTopics.length === 0) {
      toast.error("No categorized policies to download yet");
      return;
    }
    downloadTextFile(buildPolicyMarkdown(policyTopics, overallScore), policyFileName());
  };

  const handleDownloadCurrentTopic = () => {
    if (!currentTopic) {
      toast.error("No categorized policies to download yet");
      return;
    }
    downloadTextFile(buildTopicMarkdown(currentTopic), policyFileName(currentTopic));
  };

  return (
    <DocumentPreviewModal
      isOpen={isOpen}
      onClose={() => dispatch(closeDocumentPreviewModal())}
      overallScore={overallScore}
      scoreBadgeClass={getScoreBadgeClass(overallScore)}
      scoreLabel={getScoreLabel(overallScore)}
      policyTopics={policyTopics}
      activeDocTab={activeDocTab}
      setActiveDocTab={setActiveDocTab}
      formatTopicKey={formatTopicKey}
      tabDropdownOpen={tabDropdownOpen}
      setTabDropdownOpen={setTabDropdownOpen}
      suggestedOpen={suggestedOpen}
      setSuggestedOpen={setSuggestedOpen}
      issuesOpen={issuesOpen}
      setIssuesOpen={setIssuesOpen}
      suggestedItems={suggestedItems}
      checkedFeedbacks={checkedFeedbacks}
      setCheckedFeedbacks={setCheckedFeedbacks}
      answerInputs={answerInputs}
      setAnswerInputs={setAnswerInputs}
      handleApplyFeedbacks={handleApplyFeedbacks}
      isApplying={isApplying}
      handleIgnoreFeedbacks={handleIgnoreFeedbacks}
      isIgnoring={isIgnoring}
      handleApplyIssueFeedbacks={handleApplyIssueFeedbacks}
      isApplyingIssues={isApplyingIssues}
      handleIgnoreIssueFeedbacks={handleIgnoreIssueFeedbacks}
      isIgnoringIssues={isIgnoringIssues}
      issueItems={issueItems}
      topicScore={topicScore}
      topicSections={topicSections}
      renderContent={renderPolicyContent}
      onDownloadAll={handleDownloadAll}
      onDownloadCurrentTopic={handleDownloadCurrentTopic}
      currentTopicLabel={currentTopic ? formatTopicKey(currentTopic.key) : ""}
    />
  );
};

export default GlobalDocumentPreviewModal;
